import React from 'react';
import { ChannelOutput } from 'js/model/rainbow/content/ChannelOutput';
import { CmsBrowsePage } from 'js/model/cms/cms-browse-page';
import { createBrowseResultViewModel } from 'js/model/view/browse-page/browse-result';
import { VenueListingResultOutput } from 'js/model/rainbow/browse-page/VenueListingResultOutput';
import { VenueListingSpecificationOutput } from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import { BrowseResult } from 'js/pages/BrowsePage/BrowseResult';
import {
  CmsBrowseResultBanner,
  CmsBrowseResultBannerMatchType,
} from 'js/model/cms/cms-browse-result-banner';
import { parseCmsBoolean } from 'js/model/cms/boolean';
import { BrowseResultBanner } from './BrowseResultBanner';
import styles from './Results.module.css';

interface Props {
  listingSpecification: VenueListingSpecificationOutput;
  results: VenueListingResultOutput[];
  channel: ChannelOutput;
  cmsBrowsePage: CmsBrowsePage;
  onVenueEnter?: (venueId: number, rating: number) => void;
  onVenueLeave?: (venueId: number) => void;
  isInHybridView?: boolean;
  resultBannerCms: CmsBrowseResultBanner[];
}

const getMatchers = (
  banner: CmsBrowseResultBanner,
  specification: VenueListingSpecificationOutput
): {
  matchTreatment(): boolean;
  matchTreatmentType(): boolean;
} => {
  const { treatmentCategories, treatmentCategoryGroup } = specification;
  const { 'match-value': matchValue } = banner;

  return {
    matchTreatment(): boolean {
      if (matchValue && treatmentCategories) {
        const matchValues = matchValue.split(',').map(Number);

        return !!treatmentCategories.find((category) =>
          matchValues.includes(category.id)
        );
      }

      return false;
    },
    matchTreatmentType(): boolean {
      if (matchValue && treatmentCategoryGroup) {
        const matchValues = matchValue.split(',').map(Number);

        return matchValues.includes(treatmentCategoryGroup.id);
      }

      return false;
    },
  };
};

export const getMatchingBanner = (
  cms?: CmsBrowseResultBanner | CmsBrowseResultBanner[],
  specification: VenueListingSpecificationOutput = {}
): Partial<CmsBrowseResultBanner> => {
  if (cms && Array.isArray(cms)) {
    for (const banner of cms) {
      const { 'match-type': matchType } = banner;
      const { matchTreatment, matchTreatmentType } = getMatchers(
        banner,
        specification
      );

      if (parseCmsBoolean(banner.enabled)) {
        if (matchType) {
          switch (matchType) {
            case CmsBrowseResultBannerMatchType.Treatment:
              if (matchTreatment()) {
                return banner;
              }

              break;

            case CmsBrowseResultBannerMatchType.TreatmentType:
              if (matchTreatmentType()) {
                return banner;
              }

              break;
          }

          continue;
        }

        return banner;
      }
    }
  }

  return {};
};

export class Results extends React.PureComponent<Props> {
  public render(): React.ReactNode {
    const {
      listingSpecification,
      results,
      channel,
      cmsBrowsePage,
      onVenueEnter,
      onVenueLeave,
      resultBannerCms,
      isInHybridView,
    } = this.props;

    if (results.length === 0) {
      return null;
    }

    const matchingBanner = getMatchingBanner(
      resultBannerCms,
      listingSpecification
    );
    const isMatchingBannerEnabled = parseCmsBoolean(matchingBanner.enabled);

    matchingBanner.enabled;

    return (
      <div className={styles.results}>
        {results.map(
          (venueListingResult: VenueListingResultOutput, index: number) => [
            <BrowseResult
              key={venueListingResult.data.id}
              {...createBrowseResultViewModel(
                listingSpecification,
                venueListingResult,
                cmsBrowsePage,
                channel
              )}
              onVenueEnter={onVenueEnter}
              onVenueLeave={onVenueLeave}
              isInHybridView={isInHybridView}
              isLazy={index > (isMatchingBannerEnabled ? 0 : 1)}
              venueRank={index + 1}
              isPinned={venueListingResult.data.pinned}
            />,
            // renders the browse result banner after the first result and if there is more than 1 result
            index === 0 && (
              <BrowseResultBanner
                key="banner"
                isInHybridView={isInHybridView}
                matchingBanner={matchingBanner}
              />
            ),
          ]
        )}
      </div>
    );
  }
}
